// ════════════════════════════════════════════════════════════════════════════
// Chat API — Flow-Based Booking Assistant
// ════════════════════════════════════════════════════════════════════════════

const API_URL = import.meta.env.VITE_API_URL || "";

// ════════════════════════════════════════════════════════════════════════════
// Types
// ════════════════════════════════════════════════════════════════════════════

/** The two display modes shared by ChatWidget and ChatPage. */
export type WidgetMode = "chat" | "wizard";

export interface ChatWidgetContext {
  businessName: string;
  logoUrl: string | null;
  branding: {
    primaryColor: string;
    secondaryColor: string;
    backgroundColor: string;
    textColor: string;
    accentColor: string;
  };
  welcomeMessage: string;
  termsOfServiceUrl: string | null;
  privacyPolicyUrl: string | null;
}

export type FlowStep =
  | "welcome"
  | "services"
  | "providers"
  | "dates"
  | "times"
  | "contact"
  | "confirm"
  | "complete";

export interface FlowState {
  step: FlowStep;
  slug?: string;
  locationId?: string;
  serviceId?: string;
  serviceName?: string;
  servicePrice?: number;
  providerId?: string;
  providerName?: string;
  date?: string;
  time?: string;
  clientName?: string;
  clientEmail?: string;
  clientPhone?: string;
}

export interface FlowOption {
  id: string;
  label: string;
  sublabel?: string;
}

export interface FormField {
  name: string;
  label: string;
  type: string;
  required: boolean;
  placeholder?: string;
}

export interface FlowResponse {
  message: string;
  options?: FlowOption[];
  optionType?: "button" | "date" | "time" | "form";
  state: FlowState;
  formFields?: FormField[];
  confirmationCode?: string;
  paymentUrl?: string;
}

export interface ChatMessage {
  id: string;
  role: "user" | "assistant";
  content: string;
  timestamp: Date;
  options?: FlowOption[];
  optionType?: "button" | "date" | "time" | "form";
  formFields?: FormField[];
  paymentUrl?: string;
  // Only set on assistant messages from the AI chat endpoint (/message).
  citations?: AiCitation[];
  toolCalls?: AiToolCall[];
}

// AI chat mode types (chat mode → POST /api/chatbot/:slug/message)
export interface AiCitation {
  url: string;
  title: string | null;
}

export interface AiToolCall {
  name: string;
  input: Record<string, unknown>;
}

export interface AiMessageRequest {
  sessionId?: string;
  message: string;
}

export interface AiMessageResponse {
  sessionId: string;
  response: string;
  citations: AiCitation[];
  toolCalls: AiToolCall[];
  quotaExhausted?: boolean;
  quota?: { used: number; cap: number };
}

export interface AiGreetingResponse {
  greeting: string;
}

export interface AiLeadRequest {
  sessionId: string;
  name: string;
  phone?: string;
  email?: string;
  question?: string;
}

export interface AiLeadResponse {
  ok: true;
  leadId: string;
}

// ════════════════════════════════════════════════════════════════════════════
// API Functions
// ════════════════════════════════════════════════════════════════════════════

export async function getChatContext(slug: string): Promise<ChatWidgetContext> {
  const response = await fetch(`${API_URL}/api/chatbot/${slug}/context`);
  if (!response.ok) {
    const data = await response.json();
    throw new Error(data.error || "Failed to load chat context");
  }
  return response.json();
}

export async function sendFlowAction(
  slug: string,
  action: string,
  state?: FlowState,
  data?: Record<string, unknown>
): Promise<FlowResponse> {
  const response = await fetch(`${API_URL}/api/chatbot/${slug}/flow`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      action,
      state,
      data,
    }),
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.error || "Failed to process action");
  }

  return response.json();
}

export async function getChatGreeting(slug: string): Promise<AiGreetingResponse> {
  const response = await fetch(`${API_URL}/api/chatbot/${slug}/greeting`);
  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data.error || "Failed to load greeting");
  }
  return response.json();
}

export async function sendChatMessage(
  slug: string,
  body: AiMessageRequest
): Promise<AiMessageResponse> {
  const response = await fetch(`${API_URL}/api/chatbot/${slug}/message`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    const err: Error & { status?: number; retryAfterMs?: number } = new Error(
      errorData.error || "Failed to send message"
    );
    err.status = response.status;
    if (typeof errorData.retryAfterMs === "number") {
      err.retryAfterMs = errorData.retryAfterMs;
    }
    throw err;
  }
  return response.json();
}

export async function submitChatLead(
  slug: string,
  body: AiLeadRequest
): Promise<AiLeadResponse> {
  const response = await fetch(`${API_URL}/api/chatbot/${slug}/lead`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data.error || "Failed to submit lead");
  }
  return response.json();
}
